import * as React from "react";
import cn from "classnames";
import { throttle } from "lodash";
import * as Team from "src/state/modules/team";
import Styles from "./TeamNavBar.module.scss";
import { Avatar } from "src/components/atoms/Avatar/Avatar";
import { Text } from "src/components/atoms/Text/Text";
import { teamAvatarUrl } from "src/utils/team-avatar";
import PageLoader1 from "src/components/atoms/PageLoader/PageLoader";
import { usePatchTeamByIDMutation, useDeleteTeamMutation } from "src/state/modules/api";
import { ConfirmModal } from "src/components/atoms/ConfirmModal/ConfirmModal";

const TeamDetail = ({ text, className, onClick }: { text: string; className?: string; onClick?: () => void }) => {
  return (
    <div className={cn(Styles.teamDetail)} onClick={onClick}>
      <Text color="cool-grey-dark" weight="bold" className={cn(Styles.teamDetailText, className)}>
        {text}
      </Text>
    </div>
  );
};

export const TeamInfoSection = ({
  shrinkHeader,
  setShrinkHeader,
  team,
}: {
  shrinkHeader: boolean;
  setShrinkHeader: (fn: (value: boolean) => boolean) => void;
  team: Team.Team;
}) => {
  const [loading, setLoading] = React.useState(false);
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const [patchTeam] = usePatchTeamByIDMutation();
  const [deleteTeam] = useDeleteTeamMutation();
  const teamRecord = `${team?.seasonRecord}`;
  const teamSeason = `${team?.seasonName} ${team?.seasonYear}`;
  const teamLocation = `${team?.city} ${team?.state}`;

  const handleRefresh = async (id: string) => {
    // The backend PATCH (RefreshTeamWithFollowAsync) owns the whole sequence now: it follows the
    // team, waits for GameChanger to share the member list (emails), re-imports, then unfollows.
    // That replaces the old fragile client-side follow → 100ms timer → import → unfollow, which
    // raced GameChanger's follow propagation and often stored an empty user list.
    setLoading(true);
    try {
      await patchTeam(id).unwrap();
    } catch (err) {
      console.error("Failed to refresh team", err);
    } finally {
      window.location.reload();
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(true);
    await deleteTeam(id);
    // Full navigation instead of navigate() + reload(): reload() raced the router
    // and could re-request the just-deleted team's URL.
    window.location.assign("/teams");
  };

  React.useEffect(() => {
    // The app scrolls inside MainGrid's overflow container, not the window
    // (window.scrollY is always 0), so listen on the nearest scrollable ancestor.
    const findScrollParent = (node: HTMLElement | null): HTMLElement | null => {
      for (let el = node?.parentElement; el; el = el.parentElement) {
        const { overflowY } = window.getComputedStyle(el);
        if (overflowY === "auto" || overflowY === "scroll") return el;
      }
      return null;
    };
    const scroller = findScrollParent(rootRef.current);
    const getScrollY = () => (scroller ? scroller.scrollTop : window.scrollY);

    function resizeTeamNavOnScroll() {
      // Wide hysteresis band: the browser can nudge scrollTop by a few dozen px
      // after programmatic jumps (sticky/layout adjustments), so "back at the
      // top" must tolerate small offsets or the header gets stuck collapsed.
      setShrinkHeader((currentlyShrunk) => {
        if (currentlyShrunk) {
          return getScrollY() > 60;
        }
        return getScrollY() > 120;
      });
    }

    const resizeTeamNavOnScrollThrottled = throttle(resizeTeamNavOnScroll, 400);
    const target: HTMLElement | Window = scroller ?? window;
    target.addEventListener("scroll", resizeTeamNavOnScrollThrottled);
    return () => target.removeEventListener("scroll", resizeTeamNavOnScrollThrottled);
  }, [setShrinkHeader]);

  if (!team) return <PageLoader1 />;

  const teamIcon = (
    <Avatar.Team
      className={Styles.teamAvatar}
      sportIcon={Team.Sport.BASEBALL}
      source={teamAvatarUrl(team.id)}
      size={"large"}
    />
  );
  return (
    <>
      <div ref={rootRef} className={Styles.teamAvatar}>{teamIcon}</div>
      <div className={cn(Styles.teamInfo, { [Styles.shrunk]: shrinkHeader })}>
        <Text className={Styles.teamName} weight={"xbold"} color={"offBlack"}>
          {team.name}
        </Text>
        <div className={Styles.teamDetails}>
          <div className={Styles.teamMeta}>
            <TeamDetail text={teamRecord} />
            <TeamDetail text={teamSeason} />
            <TeamDetail text={teamLocation} />
          </div>
          <div className={Styles.teamActions}>
            {loading ? (
              <TeamDetail text="Loading..." />
            ) : (
              <TeamDetail text="Refresh" onClick={() => handleRefresh(team.id)} className={Styles.pointer} />
            )}
            <TeamDetail text="Delete" onClick={() => setConfirmingDelete(true)} className={Styles.pointer} />
          </div>
        </div>
        <div className={Styles.teamStaff}>
          <Text color="cool-grey-dark" size="xsmall" weight="bold">
            Staff:
          </Text>
          <Text color="cool-grey-dark" size="xsmall" weight="regular">
            {team.staff?.toString()}
          </Text>
        </div>
      </div>
      {confirmingDelete && (
        <ConfirmModal
          title="Delete Team?"
          message={`${team.name} will be removed from the app along with all of its imported games, stats, and videos. You can re-import the team later, but this cannot be undone.`}
          confirmLabel="Yes, Delete"
          confirmLoading={deleting}
          onConfirm={() => handleDelete(team.id)}
          onCancel={() => !deleting && setConfirmingDelete(false)}
        />
      )}
    </>
  );
};
